import { useState, useCallback, memo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatUrl, validateUrl } from "@/lib/utils";

const RichTextLinkConfigForm = memo(function RichTextLinkConfigForm({
  config,
  handleWidgetUpdate,
  widgetId,
}) {
  const [linkUrl, setLinkUrl] = useState(config.links?.[0]?.url || "");
  const [linkError, setLinkError] = useState("");

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!linkUrl) {
        setLinkError("Please enter a URL");
        return;
      }

      if (!validateUrl(linkUrl)) {
        setLinkError("Please enter a valid URL");
        return;
      }

      setLinkError("");

      // Update only the first link while preserving other links
      const updatedLinks = [...(config.links || [])];
      const formattedUrl = formatUrl(linkUrl);

      if (updatedLinks.length === 0) {
        updatedLinks.push({
          id: Math.random().toString(36).substr(2, 9),
          url: formattedUrl,
        });
      } else {
        updatedLinks[0] = {
          ...updatedLinks[0],
          id: updatedLinks[0].id || Math.random().toString(36).substr(2, 9),
          url: formattedUrl,
        };
      }

      // Update with proper config structure
      handleWidgetUpdate(widgetId, {
        config: {
          ...config,
          links: updatedLinks,
        },
      });
    },
    [linkUrl, handleWidgetUpdate, config, widgetId]
  );

  const handleInputChange = useCallback((e) => {
    e.stopPropagation();
    setLinkUrl(e.target.value);
    setLinkError("");
  }, []);

  // Add a handler to stop propagation on all form interactions
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className="space-y-4"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label
            htmlFor={`url-${widgetId}`}
            className="text-white"
            onClick={stopPropagation}
          >
            Enter URL
          </Label>
          <Input
            id={`url-${widgetId}`}
            value={linkUrl}
            onChange={handleInputChange}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            placeholder="example.com"
            className="mt-2 bg-white/5 border-white/10 text-white focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-white/20"
          />
          {linkError && (
            <p className="text-sm text-red-500 mt-2" onClick={stopPropagation}>
              {linkError}
            </p>
          )}
        </div>
        <button
          type="submit"
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors w-full"
        >
          Save Link
        </button>
      </form>
    </div>
  );
});

RichTextLinkConfigForm.displayName = "RichTextLinkConfigForm";

export default RichTextLinkConfigForm;
